import { useState, useMemo, useRef, useEffect } from 'react';
import type { LapEntry, AmendedLapCorner } from '@/types/telemetry';
import { formatTime, formatDelta } from '@/lib/format';
import { formatDynamicCornerLabel } from '@/lib/spline';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { CheckCircle2, Clock } from 'lucide-react';

interface SessionCornerViewProps {
  sessionId: string;
  laps: LapEntry[];
}

interface CornerDef {
  key: string;
  cornerId: string;
  orderIndex: number;
  name: string;
}

export function SessionCornerView({ sessionId, laps }: SessionCornerViewProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();
  const [selectedCornerIdx, setSelectedCornerIdx] = useState(0);
  const cornerButtonRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  // Extract distinct corners across all laps, sorted by orderIndex
  const corners: CornerDef[] = useMemo(() => {
    const map = new Map<string, CornerDef>();
    for (const l of laps) {
      for (const c of l.corners || []) {
        const key = c.cornerId || `order-${c.orderIndex}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            cornerId: c.cornerId,
            orderIndex: c.orderIndex,
            name: c.name,
          });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => a.orderIndex - b.orderIndex);
  }, [laps]);

  const activeIdx = corners.length > 0 ? Math.min(selectedCornerIdx, corners.length - 1) : 0;

  useEffect(() => {
    const btn = cornerButtonRefs.current[activeIdx];
    if (btn && typeof btn.scrollIntoView === 'function') {
      btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeIdx]);

  if (corners.length === 0) {
    return (
      <div className="card p-8 text-center text-text-muted text-sm">
        {t('session.noCornersConfigured')}
      </div>
    );
  }

  const activeCorner = corners[activeIdx];

  const getLapCorner = (l: LapEntry): AmendedLapCorner | undefined => {
    if (!l.corners) return undefined;
    return l.corners.find(c =>
      (activeCorner.cornerId && c.cornerId === activeCorner.cornerId) ||
      c.orderIndex === activeCorner.orderIndex
    );
  };

  // Compute session bests for the selected corner across valid laps
  let bestApproachKmh = 0;
  let bestEntryKmh = 0;
  let bestSlowestKmh = 0;
  let bestApexKmh = 0;
  let bestExitKmh = 0;
  let bestDurationMs = 0;

  for (const l of laps) {
    if (l.lap.lapTimeMs <= 0) continue;
    const c = getLapCorner(l);
    if (!c) continue;
    if (c.highestSpeedBeforeBrakingKmh > bestApproachKmh) bestApproachKmh = c.highestSpeedBeforeBrakingKmh;
    if (c.entrySpeedKmh > bestEntryKmh) bestEntryKmh = c.entrySpeedKmh;
    if (c.slowestSpeedKmh > bestSlowestKmh) bestSlowestKmh = c.slowestSpeedKmh;
    if (c.apexSpeedKmh > bestApexKmh) bestApexKmh = c.apexSpeedKmh;
    if (c.exitSpeedKmh > bestExitKmh) bestExitKmh = c.exitSpeedKmh;
    if (c.transitDurationMs > 0 && (bestDurationMs === 0 || c.transitDurationMs < bestDurationMs)) {
      bestDurationMs = c.transitDurationMs;
    }
  }

  const cornerLabel = (c: CornerDef, idx: number) => {
    return formatDynamicCornerLabel(c.name, idx + 1, 'T');
  };

  return (
    <div className="space-y-4 mb-6">
      {/* Corner Selector Pills */}
      <div
        role="tablist"
        aria-label={t('session.tabCorners')}
        className="flex items-center gap-1.5 overflow-x-auto no-scrollbar -mx-5 px-5 sm:mx-0 sm:px-0"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {corners.map((c, idx) => {
          const isSelected = idx === activeIdx;
          return (
            <button
              key={c.key}
              ref={el => { cornerButtonRefs.current[idx] = el; }}
              role="tab"
              aria-selected={isSelected}
              type="button"
              onClick={() => setSelectedCornerIdx(idx)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer shrink-0 whitespace-nowrap ${
                isSelected
                  ? 'bg-accent text-white shadow-sm'
                  : 'bg-bg-surface border border-border-subtle text-text-secondary hover:text-text-primary hover:bg-bg-hover'
              }`}
            >
              {cornerLabel(c, idx)}
            </button>
          );
        })}
      </div>

      {/* Per-Lap Corner Data Table */}
      <div className="card p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-4 sm:px-5 sm:pt-5 pb-4 border-b border-border-subtle">
          <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
            {cornerLabel(activeCorner, activeIdx)}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1150px] border-collapse">
            <thead>
              <tr className="divider">
                <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted sticky left-0 bg-bg-surface z-10 border-r border-border-subtle whitespace-nowrap">#</th>
                <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted whitespace-nowrap">{t('lapTable.lapTime')}</th>
                <th className="text-right py-2.5 px-4 min-w-[120px] text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted whitespace-nowrap">{t('lapAnalysis.cornerTime')}</th>
                <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted whitespace-nowrap">{t('lapTable.delta')}</th>
                <th className="text-right py-2.5 px-4 min-w-[145px] text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted whitespace-nowrap">{t('lapAnalysis.topApproachSpeed')}</th>
                <th className="text-right py-2.5 px-4 min-w-[130px] text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted whitespace-nowrap">{t('lapAnalysis.entrySpeed')}</th>
                <th className="text-right py-2.5 px-4 min-w-[130px] text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted whitespace-nowrap">{t('lapAnalysis.slowestSpeed')}</th>
                <th className="text-right py-2.5 px-4 min-w-[130px] text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted whitespace-nowrap">{t('lapAnalysis.apexSpeed')}</th>
                <th className="text-right py-2.5 px-4 min-w-[130px] text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted whitespace-nowrap">{t('lapAnalysis.exitSpeed')}</th>
                <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted whitespace-nowrap">{t('lapAnalysis.brakingDist')}</th>
                <th className="text-right py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted whitespace-nowrap">{t('lapAnalysis.trailBraking')}</th>
              </tr>
            </thead>
            <tbody>
              {laps.map((l, index) => {
                const lap = l.lap;
                const isBest = l.isBest;
                const isProcessed = l.analysisStatus === 'completed';
                const c = getLapCorner(l);

                const isBestDur = !!c && bestDurationMs > 0 && c.transitDurationMs === bestDurationMs;
                const durDeltaMs = c && bestDurationMs > 0 && c.transitDurationMs > 0
                  ? c.transitDurationMs - bestDurationMs
                  : 0;
                const isBestApproach = !!c && bestApproachKmh > 0 && Math.abs(c.highestSpeedBeforeBrakingKmh - bestApproachKmh) < 0.05;
                const isBestEntry = !!c && bestEntryKmh > 0 && Math.abs(c.entrySpeedKmh - bestEntryKmh) < 0.05;
                const isBestSlowest = !!c && bestSlowestKmh > 0 && Math.abs(c.slowestSpeedKmh - bestSlowestKmh) < 0.05;
                const isBestApex = !!c && bestApexKmh > 0 && Math.abs(c.apexSpeedKmh - bestApexKmh) < 0.05;
                const isBestExit = !!c && bestExitKmh > 0 && Math.abs(c.exitSpeedKmh - bestExitKmh) < 0.05;

                return (
                  <tr
                    key={lap.lapNumber}
                    onClick={() => navigate(`#/session/${sessionId}/lap/${lap.lapNumber}`)}
                    className={`hover:bg-bg-hover cursor-pointer transition-colors ${index < laps.length - 1 ? 'divider' : ''} ${isBest ? 'best-lap' : ''}`}
                  >
                    <td className="py-2.5 px-4 sticky left-0 bg-bg-surface z-10 border-r border-border-subtle text-text-secondary mono font-medium whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        <span>{lap.lapNumber}</span>
                        {isProcessed ? (
                          <span title={t('lapAnalysis.statusCompleted')} className="inline-flex items-center text-emerald-400">
                            <CheckCircle2 size={13} />
                          </span>
                        ) : (
                          <span title={t('lapAnalysis.statusPending')} className="inline-flex items-center text-amber-400">
                            <Clock size={13} />
                          </span>
                        )}
                        {isBest && <span className="text-green text-[0.7rem]">★</span>}
                      </span>
                    </td>
                    <td className={`py-2.5 px-4 mono font-semibold text-right whitespace-nowrap ${isBest ? 'td-green' : ''}`}>
                      {formatTime(lap.lapTimeMs)}
                    </td>
                    {c ? (
                      <>
                        <td className={`py-2.5 px-4 min-w-[120px] mono text-right whitespace-nowrap ${isBestDur ? 'td-green' : 'text-text-primary'}`}>
                          {(c.transitDurationMs / 1000).toFixed(3)} s
                        </td>
                        <td className={`py-2.5 px-4 mono text-right whitespace-nowrap ${durDeltaMs > 0 ? 'text-red' : 'text-green'}`}>
                          {c.transitDurationMs > 0 && bestDurationMs > 0 ? formatDelta(durDeltaMs) : '—'}
                        </td>
                        <td className={`py-2.5 px-4 min-w-[145px] mono text-right whitespace-nowrap ${isBestApproach ? 'td-green' : 'text-text-secondary'}`}>
                          {c.highestSpeedBeforeBrakingKmh.toFixed(1)} km/h
                        </td>
                        <td className={`py-2.5 px-4 min-w-[130px] mono text-right whitespace-nowrap ${isBestEntry ? 'td-green' : 'text-text-secondary'}`}>
                          {c.entrySpeedKmh.toFixed(1)} km/h
                        </td>
                        <td className={`py-2.5 px-4 min-w-[130px] mono text-right whitespace-nowrap ${isBestSlowest ? 'td-green' : 'text-text-primary font-semibold'}`}>
                          {c.slowestSpeedKmh.toFixed(1)} km/h
                        </td>
                        <td className={`py-2.5 px-4 min-w-[130px] mono text-right whitespace-nowrap ${isBestApex ? 'td-green' : 'text-text-secondary'}`}>
                          {c.apexSpeedKmh.toFixed(1)} km/h
                        </td>
                        <td className={`py-2.5 px-4 min-w-[130px] mono text-right whitespace-nowrap ${isBestExit ? 'td-green' : 'text-text-secondary'}`}>
                          {c.exitSpeedKmh.toFixed(1)} km/h
                        </td>
                        <td className="py-2.5 px-4 mono text-right whitespace-nowrap text-text-secondary">
                          {c.brakingDistanceM.toFixed(1)} m
                        </td>
                        <td className="py-2.5 px-4 mono text-right whitespace-nowrap text-text-secondary">
                          {c.trailBrakingScore.toFixed(0)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2.5 px-4 min-w-[120px] mono text-right text-text-muted">—</td>
                        <td className="py-2.5 px-4 mono text-right text-text-muted">—</td>
                        <td className="py-2.5 px-4 min-w-[145px] mono text-right text-text-muted">—</td>
                        <td className="py-2.5 px-4 min-w-[130px] mono text-right text-text-muted">—</td>
                        <td className="py-2.5 px-4 min-w-[130px] mono text-right text-text-muted">—</td>
                        <td className="py-2.5 px-4 min-w-[130px] mono text-right text-text-muted">—</td>
                        <td className="py-2.5 px-4 min-w-[130px] mono text-right text-text-muted">—</td>
                        <td className="py-2.5 px-4 mono text-right text-text-muted">—</td>
                        <td className="py-2.5 px-4 mono text-right text-text-muted">—</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
